import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { FaSearch, FaGift, FaTimes, FaMinus, FaPlus } from 'react-icons/fa';
import { getMyGreetingCards, sendGift } from '../../Services/User/GiftStore';
import { showToast } from '../Toast/Toast';
import CustomButton from '../CustomButton';
import './GreetingCardsModal.css';

const GreetingCardsModal = ({ show, onHide, userId }) => {
  const [greetingCards, setGreetingCards] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const imageBaseUrl = import.meta.env.VITE_REACT_APP_IMAGE_URL;

  useEffect(() => {
    if (show) {
      fetchGreetingCards();
    }
  }, [show]);

  // Search is handled client-side, no need for API call on search change

  const fetchGreetingCards = async (reset = false) => {
    try {
      setLoading(true);
      const response = await getMyGreetingCards();
      
      if (response.status && response.data) {
        const cards = Array.isArray(response.data) ? response.data : [];
        
        if (reset) {
          setGreetingCards(cards);
          setPage(1);
        } else {
          setGreetingCards(prev => [...prev, ...cards]);
        }
        
        // Initialize quantities
        const initialQuantities = {};
        cards.forEach(card => {
          if (!quantities[card.id]) {
            initialQuantities[card.id] = 1;
          }
        });
        setQuantities(prev => ({ ...prev, ...initialQuantities }));
        
        // Check if there are more items (you may need to adjust based on API response)
        setHasMore(cards.length > 0);
      }
    } catch (error) {
      console.error('Error fetching greeting cards:', error);
      showToast('Failed to load greeting cards', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (cardId, change) => {
    setQuantities(prev => {
      const currentQty = prev[cardId] || 1;
      const newQty = currentQty + change;
      return {
        ...prev,
        [cardId]: newQty < 1 ? 1 : newQty
      };
    });
  };

  const handleSendCard = async (card) => {
    if (!userId) {
      showToast('User ID is required', 'error');
      return;
    }

    try {
      setSending(prev => ({ ...prev, [card.id]: true }));
      const quantity = quantities[card.id] || 1;
      
      const response = await sendGift(card.id, quantity, userId);
      
      if (response.status) {
        showToast('Greeting card sent successfully!', 'success');
        // Reset quantity after sending
        setQuantities(prev => ({ ...prev, [card.id]: 1 }));
      } else {
        showToast(response.message || 'Failed to send card', 'error');
      }
    } catch (error) {
      console.error('Error sending greeting card:', error);
      showToast(error.message || 'Failed to send greeting card', 'error');
    } finally {
      setSending(prev => ({ ...prev, [card.id]: false }));
    }
  };

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
    fetchGreetingCards(false);
  };

  const filteredCards = greetingCards.filter(card => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      (card.title && card.title.toLowerCase().includes(searchLower)) ||
      (card.description && card.description.toLowerCase().includes(searchLower))
    );
  });

  return (
    <Modal show={show} onHide={onHide} centered className="greeting-cards-modal" size="lg">
      <Modal.Header className="greeting-cards-modal-header">
        <Modal.Title>Select Greeting Card</Modal.Title>
        <button className="modal-close-btn" onClick={onHide}>
          <FaTimes />
        </button>
      </Modal.Header>
      
      <Modal.Body className="greeting-cards-modal-body">
        {/* Search Bar */}
        <div className="greeting-cards-search">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search...."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Greeting Cards List */}
        <div className="greeting-cards-list">
          {loading && greetingCards.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">Loading greeting cards...</p>
            </div>
          ) : filteredCards.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">No greeting cards found</p>
            </div>
          ) : (
            filteredCards.map((card) => (
              <div key={card.id} className="greeting-card-item">
                <div className="card-image-wrapper">
                  <img
                    src={card.image ? `${imageBaseUrl}${card.image}` : 'https://via.placeholder.com/150'}
                    alt={card.title || 'Greeting Card'}
                    className="card-image"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/150';
                    }}
                  />
                </div>
                
                <div className="card-content">
                  <h6 className="card-title">{card.title || 'Title ABC'}</h6>
                  
                  <div className="card-actions">
                    <div className="quantity-selector">
                      <button
                        className="quantity-btn"
                        onClick={() => handleQuantityChange(card.id, -1)}
                        disabled={quantities[card.id] <= 1}
                      >
                        <FaMinus />
                      </button>
                      <span className="quantity-value">{quantities[card.id] || 1}</span>
                      <button
                        className="quantity-btn"
                        onClick={() => handleQuantityChange(card.id, 1)}
                      >
                        <FaPlus />
                      </button>
                    </div>
                    
                    <button
                      className="send-card-btn"
                      onClick={() => handleSendCard(card)}
                      disabled={sending[card.id]}
                    >
                      <FaGift className="gift-icon" />
                      <span>Send Card</span>
                    </button>
                  </div>
                  
                  <p className="card-description">
                    {card.description || 'Lorem Ipsum Dolor Sit Amet, Consectetur Adipiscing Elit. Aenean Euismod Bibendum Laoreet. Proin Gravida Dolor Sit Amet Lacus Accumsan Et Viverra Justo Commodo.'}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Load More Button */}
        {hasMore && !loading && filteredCards.length > 0 && (
          <div className="text-center mt-4">
            <CustomButton
              onClick={handleLoadMore}
              className="load-more-btn"
            >
              Load More
            </CustomButton>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default GreetingCardsModal;

